import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import api from "../../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Banknote } from "lucide-react";

const ItemsTable = ({ items, sel, toggle, kind, t, fmt }) => (
  <Table>
    <TableHeader><TableRow><TableHead className="w-8" /><TableHead>{t("inv.number")}</TableHead><TableHead>{kind === "sale" ? t("inv.client") : t("inv.supplier")}</TableHead>
      <TableHead>{t("common.dueDate")}</TableHead><TableHead className="text-right">{t("common.total")}</TableHead><TableHead className="text-right">{t("ord.residual")}</TableHead></TableRow></TableHeader>
    <TableBody>
      {items.map((i) => (
        <TableRow key={i.id} className={i.overdue ? "bg-red-500/5" : ""} data-testid={`open-item-${i.id}`}>
          <TableCell><input type="checkbox" checked={!!sel[i.id]} onChange={() => toggle(i)} data-testid={`open-item-check-${i.id}`} /></TableCell>
          <TableCell className="font-mono text-xs">{i.number || "—"}</TableCell>
          <TableCell className="text-sm">{i.counterparty}</TableCell>
          <TableCell className="font-mono text-xs">{i.due_date || "—"} {i.overdue && <Badge variant="destructive" className="ml-1 text-[10px]">+{i.days_overdue}g</Badge>}</TableCell>
          <TableCell className="text-right font-mono text-xs">{fmt(i.total)}</TableCell>
          <TableCell className="text-right font-mono text-sm font-semibold">{fmt(i.residual)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const OpenItemsTab = ({ accounts, onSettled }) => {
  const { t, fmt, entity } = useApp();
  const [data, setData] = useState({ receivables: [], payables: [], total_receivables: 0, total_payables: 0 });
  const [sel, setSel] = useState({});
  const [dlg, setDlg] = useState(null);

  const load = useCallback(() => api.get(`/ledger/open-items?entity_id=${entity}`).then((r) => { setData(r.data); setSel({}); }).catch(() => {}), [entity]);
  useEffect(() => { load(); }, [load]);

  const toggle = (i) => setSel((s) => { const n = { ...s }; if (n[i.id]) delete n[i.id]; else n[i.id] = { ...i, amount: String(i.residual) }; return n; });
  const selected = Object.values(sel);
  const kinds = new Set(selected.map((x) => x.kind));
  const finAccounts = accounts.filter((a) => ["1000", "1010", "1020"].includes(a.code) || (a.type === "asset" && /banca|cassa|carta/i.test(a.name)));

  const openSettle = () => setDlg({ date: new Date().toISOString().slice(0, 10), account_code: "1000", description: "" });
  const total = selected.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const settle = async () => {
    try {
      await api.post("/ledger/settle", { entity_id: entity, date: dlg.date, account_code: dlg.account_code, description: dlg.description,
        items: selected.map((x) => ({ invoice_id: x.id, amount: parseFloat(x.amount) || 0 })) });
      toast.success(t("ord.settledOk")); setDlg(null); load(); onSettled?.();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };

  return (
    <div className="space-y-4" data-testid="open-items-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="text-xs" data-testid="total-receivables">{t("ord.receivables")}: <span className="font-mono ml-1">{fmt(data.total_receivables)}</span></Badge>
        <Badge variant="outline" className="text-xs" data-testid="total-payables">{t("ord.payables")}: <span className="font-mono ml-1">{fmt(data.total_payables)}</span></Badge>
        <div className="flex-1" />
        <Button onClick={openSettle} disabled={selected.length === 0 || kinds.size > 1} data-testid="settle-button"><Banknote className="h-4 w-4 mr-2" />{t("ord.settle")} ({selected.length})</Button>
      </div>
      {kinds.size > 1 && <p className="text-xs text-destructive">{t("ord.mixedKinds")}</p>}
      <div className="grid gap-6 xl:grid-cols-2">
        <Card><CardHeader><CardTitle className="text-base">{t("ord.receivables")}</CardTitle></CardHeader>
          <CardContent className="p-0">{data.receivables.length === 0 ? <div className="p-6 text-sm text-muted-foreground">{t("common.noData")}</div> : <ItemsTable items={data.receivables} sel={sel} toggle={toggle} kind="sale" t={t} fmt={fmt} />}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-base">{t("ord.payables")}</CardTitle></CardHeader>
          <CardContent className="p-0">{data.payables.length === 0 ? <div className="p-6 text-sm text-muted-foreground">{t("common.noData")}</div> : <ItemsTable items={data.payables} sel={sel} toggle={toggle} kind="purchase" t={t} fmt={fmt} />}</CardContent></Card>
      </div>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="settle-dialog">
          <DialogHeader><DialogTitle>{t("ord.settle")}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("common.date")}</Label><Input type="date" value={dlg.date} onChange={(e) => setDlg({ ...dlg, date: e.target.value })} data-testid="settle-date-input" /></div>
                <div><Label>{t("ord.finAccount")}</Label>
                  <Select value={dlg.account_code} onValueChange={(v) => setDlg({ ...dlg, account_code: v })}><SelectTrigger data-testid="settle-account-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(finAccounts.length ? finAccounts : accounts).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div><Label>{t("common.description")}</Label><Input value={dlg.description} onChange={(e) => setDlg({ ...dlg, description: e.target.value })} placeholder={t("ord.autoDesc")} data-testid="settle-description-input" /></div>
              <div className="space-y-2">
                {selected.map((x) => (
                  <div key={x.id} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs w-28 truncate">{x.number}</span><span className="flex-1 truncate">{x.counterparty}</span>
                    <Input className="h-8 w-32 text-right font-mono" type="number" step="0.01" max={x.residual} value={x.amount}
                      onChange={(e) => setSel({ ...sel, [x.id]: { ...x, amount: e.target.value } })} data-testid={`settle-amount-${x.id}`} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-sm font-semibold border-t border-border pt-2"><span>{t("common.total")}</span><span className="font-mono" data-testid="settle-total">{fmt(total)}</span></div>
              <Button className="w-full" onClick={settle} disabled={total <= 0} data-testid="settle-confirm-button">{t("ord.confirmSettle")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
